import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';

import { Component, inject, OnInit } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { Title } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { Category, Product } from '@infrab4a/connect';
import { CatalogService } from '@infrab4a/connect-angular';
import { MessageService } from 'primeng/api';
import { ShopSettingsService } from 'src/app/connect-api/api/shop-settings/shop-settings.service';
import { AppDialogService } from 'src/app/services/dialog.service';
import { LoaderService } from 'src/app/services/loader.service';
import {
  getMensNewHomeEditRoute,
  mapMensNewHomeDisplayRuleText,
  MENSMARKET_HOME_DOCUMENT_ID
} from './mens-new-home.constants';

@Component({
  selector: 'app-mens-new-home',
  templateUrl: './mens-new-home.component.html',
  styleUrl: './mens-new-home.component.scss'
})
export class MensNewHomeComponent implements OnInit {
  private shopSettingsService = inject(ShopSettingsService);
  private messageService = inject(MessageService);
  private router = inject(Router);
  private title = inject(Title);

  settings: any;
  sections: any[] = [];
  productsByCategoryId: Record<string, Product[]> = {};
  loadingProductsByCategoryId: Record<string, boolean> = {};
  menuItems = [
    { label: 'Adicionar Banner', routerLink: ['banner'] },
    { label: 'Adicionar Coleção', routerLink: ['collection'] }
  ];
  form = new FormGroup({});

  constructor(readonly catalogService: CatalogService) {}

  async ngOnInit(): Promise<void> {
    this.title.setTitle('Customizar Home da Mensmarket');
    LoaderService.showLoader();
    try {
      this.settings = await this.shopSettingsService.getById(
        MENSMARKET_HOME_DOCUMENT_ID
      );
      this.sections = this.settings?.sections ?? [];
      void this.loadCollectionProducts();
    } catch (error) {
      AppDialogService.showErrorDialog(error);
    } finally {
      LoaderService.showLoader(false);
    }
  }

  async update(): Promise<void> {
    LoaderService.showLoader();
    try {
      await this.shopSettingsService.update({
        id: this.settings?.id || MENSMARKET_HOME_DOCUMENT_ID,
        sections: this.sections
      });
      this.messageService.add({
        severity: 'success',
        summary: 'Sucesso',
        detail: 'Página reordenada com sucesso'
      });
    } catch (error) {
      AppDialogService.showErrorDialog(error);
    } finally {
      LoaderService.showLoader(false);
    }
  }

  removeSection(index: number): void {
    this.sections.splice(index, 1);
    void this.update();
  }

  async onDrop(event: CdkDragDrop<any[]>): Promise<void> {
    const dragged = this.sections[event.previousIndex];
    const target = this.sections[event.currentIndex];
    if (dragged.fixed || target.fixed) {
      return;
    }
    moveItemInArray(this.sections, event.previousIndex, event.currentIndex);
    await this.update();
  }

  editSection(index: number): void {
    const section = this.sections[index];
    const route = getMensNewHomeEditRoute(section.type, section.id);
    if (route) {
      void this.router.navigate([route]);
    }
  }

  mapDisplayRuleText(ruleObj: Record<string, boolean>): string {
    return mapMensNewHomeDisplayRuleText(ruleObj);
  }

  async getProducts(id: string): Promise<Product[]> {
    if (!id) {
      return [];
    }

    return this.catalogService
      .fetchProducts({
        category: Category.toInstance({ id }),
        perPage: 8,
        page: 0,
        sort: 'most-relevant'
      })
      .then((res) => res.products.data || [])
      .catch(() => []);
  }

  private async loadCollectionProducts(): Promise<void> {
    const categoryIds = this.sections
      ?.filter((section) => section.type === 'COLLECTION' && section.categoryId)
      .map((section) => section.categoryId);

    if (!categoryIds?.length) {
      return;
    }

    for (const categoryId of [...new Set(categoryIds)]) {
      if (
        this.loadingProductsByCategoryId[categoryId] ||
        this.productsByCategoryId[categoryId]
      ) {
        continue;
      }

      this.loadingProductsByCategoryId[categoryId] = true;

      try {
        this.productsByCategoryId[categoryId] =
          await this.getProducts(categoryId);
      } finally {
        this.loadingProductsByCategoryId[categoryId] = false;
      }
    }
  }
}
